import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import type { VerificationInfo } from 'webapp/utils/verification';

import type {
	BoardSelectListItem,
	BoardSelectListSelection,
} from 'webapp/components/boards/popup-select-list';

import Board from 'webapp/models-custom/board';
import BoardFromAnotherSpace from 'webapp/models-custom/board-from-another-space';
import BoardLoop from 'webapp/models-custom/board-loop';
import Report from 'webapp/models-custom/report';

export interface Args {
	model: BoardSelectListItem;
	selected: BoardSelectListSelection;
	checkIfItemCanBeSelected: (item: BoardSelectListItem) => boolean;
	onSelect: (item: BoardSelectListItem) => void;
	hideDatasourceColumn?: boolean;
}

export default class BoardsSelectBaseItemComponent<T extends Args> extends Component<T> {
	@service('account') declare accountService: Service.Account;

	@tracked isVisibleOnScreen = true;
	lazyLoadScrollContainerSelector = '.popup-type-boards-select-list .content';

	get isChecked(): boolean {
		const { selected, model } = this.args;

		let selectedArray: keyof BoardSelectListSelection = 'boardsFromAnotherSpace';
		if (model instanceof Board) {
			selectedArray = 'boards';
		} else if (model instanceof BoardLoop) {
			selectedArray = 'boardLoops';
		} else if (model instanceof Report) {
			selectedArray = 'reports';
		}

		return (selected[selectedArray] as BoardSelectListItem[])
			.map((item) => item.id)
			.includes(model.id);
	}

	get isBoardFromAnotherSpace() {
		return this.isModelBoardFromAnotherSpace(this.args.model);
	}

	get boardFromAnotherSpaceSourceWarning() {
		const model = this.args.model;
		if (this.isModelBoardFromAnotherSpace(model) && !model.allSourceTypesAvailable) {
			let text =
				'There are data sources used in this template that are missing in the account where this dashboard is created. This might cause built metrics to show up as "Undefined" or "Empty." To avoid this, connect the necessary data sources in the account or adjust the template.';

			if (!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins) {
				text +=
					'Learn more <a href="https://help.databox.com/create-a-template" target="_blank">here</a>';
			}

			return text;
		}
		return '';
	}

	get modelAsBoardFromAnotherSpace() {
		return this.args.model as BoardFromAnotherSpace;
	}

	get modelAsBoardOrBoardLoop() {
		return this.args.model as Board | BoardLoop;
	}

	get activeFilter() {
		if (this.isBoardFromAnotherSpace) {
			return '';
		} else {
			return this.modelAsBoardOrBoardLoop.activeFilter;
		}
	}

	get color() {
		if (this.isBoardFromAnotherSpace) {
			return '';
		} else {
			return this.modelAsBoardOrBoardLoop.color;
		}
	}

	get themeId() {
		const model = this.args.model;
		if (model instanceof Board) {
			return model.themeId;
		} else {
			return 0;
		}
	}

	get favorite() {
		if (this.isBoardFromAnotherSpace) {
			return false;
		} else {
			return this.modelAsBoardOrBoardLoop.favorite;
		}
	}

	get boardCount() {
		const model = this.args.model;
		if (model instanceof BoardLoop) {
			return model.boardCount;
		} else {
			return 0;
		}
	}

	get verificationInfo(): VerificationInfo | undefined {
		const model = this.args.model;
		if ('verificationInfo' in model) {
			return model.verificationInfo;
		}
		return undefined;
	}

	get name(): string {
		const model = this.args.model;
		if (model instanceof Report) {
			return model.title;
		} else {
			return model.name;
		}
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}

	@action
	beforeSelect(): Promise<void> {
		if (this.args.checkIfItemCanBeSelected(this.args.model)) {
			return Promise.resolve();
		} else {
			return Promise.reject();
		}
	}

	@action
	select(item: BoardSelectListItem, event: Event): void {
		event.preventDefault();

		void this.beforeSelect().then(() => {
			this.args.onSelect(item);
		});
	}

	protected isModelBoardFromAnotherSpace(
		model: BoardSelectListItem,
	): model is BoardFromAnotherSpace {
		return model instanceof BoardFromAnotherSpace;
	}
}
